import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { Activity, TrendingUp, DollarSign, Zap, AlertCircle, CheckCircle } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface Metrics {
  avgResponseTime: number;
  cacheHitRate: number;
  totalCost: number;
  errors: number;
  totalRequests: number;
  modelUsage: Record<string, number>;
  slowRequests: Array<{
    operation: string;
    duration: number;
    timestamp: number;
  }>;
}

export default function AdminAIMetrics() {
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const { data, error } = await supabase.functions.invoke('ai-metrics');
        if (error) throw error;
        setMetrics(data);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
    const interval = setInterval(fetchMetrics, 10000); // Update every 10s

    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Activity className="w-12 h-12 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading metrics...</p>
        </div>
      </div>
    );
  }

  if (!metrics) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Card className="w-96">
          <CardContent className="pt-6">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <p className="text-center text-muted-foreground">Failed to load metrics</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const errorRate = metrics.totalRequests > 0 ? metrics.errors / metrics.totalRequests : 0;

  return (
    <div className="container mx-auto p-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">AI Performance Metrics</h1>
        <p className="text-muted-foreground">Real-time monitoring of AI operations</p>
      </div>

      {/* Key Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Avg Response Time */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.avgResponseTime}ms</div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: &lt;2000ms
            </p>
            <Progress
              value={Math.min((metrics.avgResponseTime / 2000) * 100, 100)}
              className="mt-2"
            />
            {metrics.avgResponseTime < 2000 ? (
              <div className="flex items-center mt-2 text-xs text-green-600">
                <CheckCircle className="w-3 h-3 mr-1" /> On target
              </div>
            ) : (
              <div className="flex items-center mt-2 text-xs text-yellow-600">
                <AlertCircle className="w-3 h-3 mr-1" /> Above target
              </div>
            )}
          </CardContent>
        </Card>

        {/* Cache Hit Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Cache Hit Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(metrics.cacheHitRate * 100).toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Target: &gt;70%
            </p>
            <Progress
              value={metrics.cacheHitRate * 100}
              className="mt-2"
            />
            {metrics.cacheHitRate > 0.7 ? (
              <div className="flex items-center mt-2 text-xs text-green-600">
                <CheckCircle className="w-3 h-3 mr-1" /> On target
              </div>
            ) : (
              <div className="flex items-center mt-2 text-xs text-yellow-600">
                <AlertCircle className="w-3 h-3 mr-1" /> Below target
              </div>
            )}
          </CardContent>
        </Card>

        {/* Total Cost */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Cost</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${metrics.totalCost.toFixed(2)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Lifetime spending
            </p>
          </CardContent>
        </Card>

        {/* Error Rate */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {(errorRate * 100).toFixed(2)}%
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {metrics.errors} errors / {metrics.totalRequests} requests
            </p>
            <Progress
              value={Math.min(errorRate * 10000, 100)}
              className="mt-2"
            />
            {errorRate < 0.01 ? (
              <div className="flex items-center mt-2 text-xs text-green-600">
                <CheckCircle className="w-3 h-3 mr-1" /> On target
              </div>
            ) : (
              <div className="flex items-center mt-2 text-xs text-red-600">
                <AlertCircle className="w-3 h-3 mr-1" /> Above target
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Model Usage */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Model Usage Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Object.entries(metrics.modelUsage).map(([model, count]) => {
              const percentage = (Number(count) / metrics.totalRequests) * 100;
              return (
                <div key={model}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">{model}</span>
                    <span className="text-sm text-muted-foreground">
                      {count} ({percentage.toFixed(1)}%)
                    </span>
                  </div>
                  <Progress value={percentage} />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Slow Requests */}
      {metrics.slowRequests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recent Slow Requests (&gt;5s)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-2 font-medium">Operation</th>
                    <th className="text-left p-2 font-medium">Duration</th>
                    <th className="text-left p-2 font-medium">Time</th>
                  </tr>
                </thead>
                <tbody>
                  {metrics.slowRequests.map((req, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-2">{req.operation}</td>
                      <td className="p-2 text-red-600 font-semibold">
                        {req.duration}ms
                      </td>
                      <td className="p-2 text-muted-foreground">
                        {new Date(req.timestamp).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
